import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

function buildPortalInviteUrl(inviteToken) {
  return `${window.location.origin}/#/portal/invite/${inviteToken}`
}

async function copyText(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (err) {
    console.warn('Clipboard API failed, using fallback', err)
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.top = '-1000px'
    textarea.style.left = '-1000px'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)
    return copied
  } catch (err) {
    console.error('Fallback copy failed', err)
    return false
  }
}

export default function ClientPortalManager() {
  const [portalAccess, setPortalAccess] = useState([])
  const [invites, setInvites] = useState([])
  const [sessions, setSessions] = useState([])

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadAll()
  }, [])

  async function loadAll() {
    try {
      setLoading(true)
      setError('')

      const [accessRes, invitesRes, sessionsRes] = await Promise.all([
        supabase.rpc('codevaro_client_portal_access_list', { p_client_id: null }),
        supabase.rpc('codevaro_client_portal_invites_admin_list', { p_client_id: null }),
        supabase.rpc('codevaro_client_portal_sessions_admin_list', { p_client_id: null })
      ])

      if (accessRes.error) throw accessRes.error
      if (invitesRes.error) throw invitesRes.error
      if (sessionsRes.error) throw sessionsRes.error

      setPortalAccess(accessRes.data || [])
      setInvites(invitesRes.data || [])
      setSessions(sessionsRes.data || [])
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα φόρτωσης portal management: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const filteredAccess = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()

    return portalAccess.filter((row) => {
      if (!term) return true
      return (
        row.client_name?.toLowerCase().includes(term) ||
        row.contact_name?.toLowerCase().includes(term) ||
        row.email?.toLowerCase().includes(term)
      )
    })
  }, [portalAccess, searchTerm])

  async function handleCreateInvite(accessId) {
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const { data, error: rpcError } = await supabase.rpc('codevaro_client_portal_invite_create', {
        p_portal_access_id: accessId,
        p_expires_hours: 72
      })

      if (rpcError) throw rpcError

      const invite = data?.[0]
      await loadAll()

      if (invite?.invite_token) {
        const fullUrl = buildPortalInviteUrl(invite.invite_token)
        const copied = await copyText(fullUrl)

        if (copied) {
          setSuccess(`Το invite δημιουργήθηκε και αντιγράφηκε:\n${fullUrl}`)
        } else {
          setSuccess(`Το invite δημιουργήθηκε:\n${fullUrl}`)
        }
      } else {
        setSuccess('Το invite δημιουργήθηκε επιτυχώς.')
      }
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα δημιουργίας invite: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleCopyInvite(inviteToken) {
    try {
      setError('')
      setSuccess('')

      const fullUrl = buildPortalInviteUrl(inviteToken)
      const copied = await copyText(fullUrl)

      if (copied) {
        setSuccess(`Το invite link αντιγράφηκε:\n${fullUrl}`)
      } else {
        setSuccess(`Το invite link είναι:\n${fullUrl}`)
      }
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα αντιγραφής invite link: ${err.message}`)
    }
  }

  async function handleRevokeSession(sessionId) {
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_client_portal_session_revoke', {
        p_session_id: sessionId
      })

      if (rpcError) throw rpcError

      await loadAll()
      setSuccess('Το session ανακλήθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα ανάκλησης session: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  function statusBadge(active) {
    return active
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800'
  }

  function sessionBadge(revokedAt, expiresAt) {
    if (revokedAt) return 'bg-red-100 text-red-800'
    if (expiresAt && new Date(expiresAt) < new Date()) return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Client Portal Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Διαχείριση portal access, invites και active sessions.
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 whitespace-pre-wrap">
          {success}
        </div>
      ) : null}

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Αναζήτηση</label>
        <div className="relative">
          <SafeIcon
            icon={FiIcons.FiSearch}
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Πελάτης, επαφή, email..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Portal Access</h2>

        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : filteredAccess.length === 0 ? (
          <p className="text-sm text-gray-500">Δεν βρέθηκαν portal access records.</p>
        ) : (
          <div className="space-y-3">
            {filteredAccess.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="font-medium text-gray-900">{row.client_name || '—'}</div>
                  <div className="text-sm text-gray-600">
                    {row.contact_name || '—'} · {row.email || '—'}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadge(row.is_active)}`}>
                      {row.is_active ? 'Ενεργό' : 'Ανενεργό'}
                    </span>
                    {row.last_login_at ? (
                      <span className="text-xs text-gray-500">
                        Last login: {new Date(row.last_login_at).toLocaleString('el-GR')}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleCreateInvite(row.id)}
                    disabled={saving || !row.is_active}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    Δημιουργία Invite
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Invites</h2>

        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : invites.length === 0 ? (
          <p className="text-sm text-gray-500">Δεν υπάρχουν invites.</p>
        ) : (
          <div className="space-y-3">
            {invites.map((invite) => (
              <div
                key={invite.invite_id}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="font-medium text-gray-900">{invite.client_name || '—'}</div>
                  <div className="text-sm text-gray-600">{invite.email || '—'}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    Expires: {invite.expires_at ? new Date(invite.expires_at).toLocaleString('el-GR') : '—'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {invite.used_at
                      ? `Used: ${new Date(invite.used_at).toLocaleString('el-GR')}`
                      : 'Not used yet'}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleCopyInvite(invite.invite_token)}
                    className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Sessions</h2>

        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-gray-500">Δεν υπάρχουν sessions.</p>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="font-medium text-gray-900">{session.client_name || '—'}</div>
                  <div className="text-sm text-gray-600">{session.email || '—'}</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${sessionBadge(session.revoked_at, session.expires_at)}`}>
                      {session.revoked_at ? 'Revoked' : 'Active'}
                    </span>
                    <span className="text-xs text-gray-500">
                      Expires: {session.expires_at ? new Date(session.expires_at).toLocaleString('el-GR') : '—'}
                    </span>
                    {session.last_seen_at ? (
                      <span className="text-xs text-gray-500">
                        Last seen: {new Date(session.last_seen_at).toLocaleString('el-GR')}
                      </span>
                    ) : null}
                  </div>
                </div>

                {!session.revoked_at ? (
                  <button
                    onClick={() => handleRevokeSession(session.session_id)}
                    disabled={saving}
                    className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    Revoke Session
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}